import { useState, useRef } from 'react'
import QRCode from 'qrcode'
import { Toast } from 'antd-mobile'

import styles from './HomeShare.module.less'
import closeIcon from './assets/close.svg'
import googleIcon from './assets/google.svg'
import bingIcon from './assets/bing.svg'
import wechatIcon from './assets/wechat.svg'
import zaloIcon from './assets/zalo.svg'
import defafultCorpLogo from '@/assets/img/default/corp_avatar.png'

import { t } from '@/utils/i18n'
import { utils, image, storageUtil, uploadCommonEt } from '@/utils'
import useCorpInfoStore from '@/stores/corp-info'
import { getConfig } from '@/config/config'

const { to, downloadFile, getUuid, getImageBase64, throttle, urlAddParams } =
  utils
const { htmlElement2Png } = image
const { getLocalStorage } = storageUtil

interface IProps {
  onClose: () => void
}

function HomeShare(props: IProps) {
  const homeShareRef = useRef(null)
  const modalRef = useRef(null)

  const [qrcodeUrl, setQrcodeUrl] = useState('')
  const [corpLogoBase64, setCorpLogoBase64] = useState('')
  const [enableDownload, setEnableDownload] = useState(false)
  const { corpInfo } = useCorpInfoStore()
  const { corpName, corpLogo } = corpInfo
  const canShare = window.webview && window.webview.systemShare
  const config = getConfig()
  const { site, ossRoot } = config

  let shareUrl = ''

  // 关闭弹窗
  const handleClose = () => {
    props.onClose()
  }

  // 点击蒙层时
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === modalRef.current) {
      handleClose()
    }
  }

  // 下载图片
  const downloadPng = throttle(async () => {
    if (!homeShareRef.current) {
      return
    }

    // 7.8.3需求埋点：点击【分享】按钮弹出弹层后点击【下载图片】
    uploadCommonEt(14)

    const createToast = Toast.show({
      icon: 'loading',
      content: t('生成图片中…'),
      duration: 0,
    })

    const homeSharePng = await htmlElement2Png(homeShareRef.current)

    createToast.close()
    handleClose()

    // app下载
    if (window.webview && window.webview.downloadMedia) {
      const params = JSON.stringify([
        {
          path: homeSharePng.replace(/^data:image\/\w+;base64,/, ''),
          name: `${getUuid()}.png`,
          isUrl: false,
          isVideo: false,
        },
      ])

      window.webview.downloadMedia(params)
      return
    }

    // 浏览器下载
    const downloadToast = Toast.show({
      icon: 'loading',
      content: t('正在下载'),
      duration: 0,
    })

    downloadFile(homeSharePng, `${getUuid()}.png`)
    downloadToast.close()
  }, 1000)

  // 分享链接
  const shareHomeUrl = throttle(() => {
    if (canShare) {
      handleClose()

      window.webview.systemShare(`${t('新品分享，点击查看')}，${shareUrl}`)
    }
  }, 1000)

  /**
   * 生成二维码并设置二维码URL。
   *
   * @returns {Promise<void>} 一个 Promise，表示生成二维码的异步操作。
   */
  async function setQrcode(): Promise<void> {
    const [err, res] = await to(
      QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 0.5,
      }),
    )

    if (err) {
      console.error(err)
      return
    }

    setQrcodeUrl(res)
  }

  /**
   * 设置公司Logo并将其转换为Base64格式。
   *
   */
  async function setLogoImage() {
    let imageUrl = defafultCorpLogo

    if (corpLogo) {
      imageUrl = `${ossRoot}${corpLogo}?x-oss-process=image/resize,m_fill,w_110`
    }

    const [err, res] = await to(getImageBase64(imageUrl))

    if (err) {
      console.error(err)
      return
    }

    setCorpLogoBase64(res)
  }

  // 初始化
  async function init() {
    const { origin, href } = location

    shareUrl = urlAddParams(href.replace(origin, site), {
      language: getLocalStorage('language') || 'en-US',
      isShare: 1, // 用于区分是否通过分享链接进入
    })

    await setQrcode()
    await setLogoImage()

    setTimeout(() => {
      setEnableDownload(true)
    }, 0)
  }

  init()

  return (
    <div className={styles['mask']} ref={modalRef} onClick={handleOverlayClick}>
      <div className={styles['modal']}>
        <img
          className={styles['close']}
          title="close"
          src={closeIcon}
          onClick={handleClose}
        />

        <div className={styles['main']} ref={homeShareRef}>
          {/* 网站信息 */}
          <div className={styles['site']}>
            <img
              className={styles['site-logo']}
              src={corpLogoBase64}
              title="logo"
            />

            <div className={styles['site-info']}>
              <div className={styles['site-title']}>{corpName}</div>
              <div className={styles['site-desc']}>{t('新品分享')}</div>
            </div>
          </div>

          {/* 二维码 */}
          <div className={styles['share']}>
            <img
              className={styles['share-img']}
              src={qrcodeUrl}
              title="qrcode"
            />

            <div className={styles['share-app']}>
              <img
                className={styles['share-icon']}
                src={googleIcon}
                title="google"
              />
              <img
                className={styles['share-icon']}
                src={bingIcon}
                title="bing"
              />
              <img
                className={styles['share-icon']}
                src={wechatIcon}
                title="wechat"
              />
              <img
                className={styles['share-icon']}
                src={zaloIcon}
                title="zalo"
              />
            </div>

            <div className={styles['share-tips']}>{t('扫码查看')}</div>
          </div>
        </div>

        {/* 动作按钮 */}
        <div className={styles['footer']}>
          <div
            className={`${styles['dl-btn']} ${
              enableDownload ? '' : styles['disabled']
            }`}
            onClick={downloadPng}
          >
            {t('下载图片')}
          </div>

          {canShare && (
            <div className={styles['share-btn']} onClick={shareHomeUrl}>
              {t('分享链接')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HomeShare
